/**
 * Helper Class to fill a list of Share Links with dynamic URLs
 * 
 * @example
 * <ul data-js-share>
 *  <li><a href="#" data-js-share-element="facebook">Share on Facebook</a></li>
 *  <li><a href="#" data-js-share-element="email">Share via E-Mail</a></li>
 * </ul>
 */
class ShareHelper {

  /**
   * Constructs a ShareHelper instance.
   * @param {HTMLElement} wrapper the wrapper element with [data-js-share]
   */
  constructor(wrapper) {
    /** @type {HTMLElement} */
    this.wrapper = wrapper;

    /** @type {NodeListOf<HTMLElement>} */
    this.anchorElements = this.wrapper.querySelectorAll('[data-js-share-element]');
    this.populateAnchorElements();

    this.wrapper.dataset.jsShareInitialized = 'true';
  }

  /**
   * Populates share anchor elements with the appropriate URL.
   */
  populateAnchorElements() {
    this.anchorElements.forEach(anchorElement => {
        const shareType = anchorElement.dataset.jsShareElement;
        let shareUrl = '#';

      switch (shareType) {
        case 'facebook':
          shareUrl = this.constructor.getFacebookShareUrl();
          break;
        case 'pinterest':
          shareUrl = this.constructor.getPinterestShareUrl();
          break;
        case 'linkedin':
          shareUrl = this.constructor.getLinkedinShareUrl();
          break;
        case 'email':
          shareUrl = this.constructor.getEmailShareUrl();
          break;
        case 'whatsapp':
          shareUrl = this.constructor.getWhatsappShareUrl();
          break;
        default:
          console.warn(`Unknown share type: ${shareType}`);
      }

      anchorElement.setAttribute('href', shareUrl);
      anchorElement.setAttribute('target', '_blank');
      anchorElement.setAttribute('rel', 'noopener noreferrer');
    });
  }

  /**
   * Returns the base share URL.
   * @static
   * @returns {string}
   */
  static getBaseShareUrl() {
    return window.location.href;
  }

  /**
   * Returns the base share title.
   * @static
   * @returns {string}
   */
  static getBaseShareTitle(){
    return document.title;
  }

  /**
   * Returns the base share media URL if present.
   * @static
   * @returns {string}
   */
  static getBaseShareMedia(){
    const metaOgImage = document.querySelector('meta[property="og:image"]');
    return metaOgImage ? metaOgImage.getAttribute('content') : '';
  }

  /**
   * Returns the Facebook share URL.
   * @static
   * @returns {string}
   */
  static getFacebookShareUrl() {
    const facebookUrl = new URL("https://www.facebook.com/sharer/sharer.php");
    facebookUrl.searchParams.set('u', this.getBaseShareUrl());
    return facebookUrl.toString();
  }

  /**
   * Returns the Pinterest share URL.
   * @static
   * @returns {string}
   */
  static getPinterestShareUrl() {
    const pinterestUrl = new URL("https://www.pinterest.com/pin/create/button/");
    pinterestUrl.searchParams.set('url', this.getBaseShareUrl());
    pinterestUrl.searchParams.set('media', this.getBaseShareMedia());
    pinterestUrl.searchParams.set('description', this.getBaseShareTitle());
    return pinterestUrl.toString();
  }

  /**
   * Returns the LinkedIn share URL.
   * @static
   * @returns {string}
   */
  static getLinkedinShareUrl() {
    const linkedinUrl = new URL("https://www.linkedin.com/shareArticle?mini=true");
    linkedinUrl.searchParams.set('url', this.getBaseShareUrl());
    return linkedinUrl.toString();
  }

  /**
   * Returns the email share URL.
   * @static
   * @returns {string}
   */
  static getEmailShareUrl() {
    const emailUrl = new URL("mailto:");
    emailUrl.searchParams.set('subject', `www.erco.com - ${this.getBaseShareTitle()}`);
    emailUrl.searchParams.set('body', `${this.getBaseShareTitle()}\n\n${this.getBaseShareUrl()}`);

    emailUrl.search = emailUrl.searchParams.toString().replace(/\+/g, '%20'); // Encode spaces as %20
    return emailUrl.toString();
  }

  /**
   * Returns the WhatsApp share URL.
   * @static
   * @returns {string}
   */
  static getWhatsappShareUrl() {
    const whatsappUrl = new URL("whatsapp://send");
    whatsappUrl.searchParams.set('text', this.getBaseShareUrl());
    return whatsappUrl.toString();
  }

  /**
   * Initializes the ShareHelper for all share elements.
   * @static
   * @param {boolean} isForce 
   */
  static init(isForce = false) {
    const shareWrappers = document.querySelectorAll('[data-js-share]');

    shareWrappers.forEach(wrapper => {
      if (!wrapper.dataset.jsShareInitialized || isForce) {
        new ShareHelper(wrapper);
      }
    })
  }
}

window.ShareHelper = ShareHelper;

document.addEventListener('DOMContentLoaded', () => {
  ShareHelper.init();
});
